/* ============================================================================
   ICHS PRICE / RENDER DEBUG  —  paste into the theme's assets/custom.js
   (or add as its own asset and include before </body> in theme.liquid).

   PURPOSE: stop guessing. On any page it tells us exactly what we're looking
   at — theme, template, sections — and for every product card: the price the
   shopper sees, the raw data-price, the money format used, and the REAL price
   from Shopify — flagging every card where shown != real (e.g. the $1,999 on a
   $0.00 foot).

   SAFE FOR PRODUCTION: does nothing unless you opt in. Turn on with either
     • add  ?ichs_debug=1  to the URL, or
     • run  localStorage.setItem('ichsDebug','1')  once in the console.
   Turn off:  localStorage.removeItem('ichsDebug')  (and drop the query param).

   OUTPUT: a console group (console.table of every card) + a small fixed panel
   bottom-right listing theme/template/sections and any price mismatches.
   Re-scans automatically when Globo re-renders the grid.
   ========================================================================== */
(function () {
  'use strict';

  var ON = /[?&]ichs_debug=1/.test(location.search) ||
           (window.localStorage && localStorage.getItem('ichsDebug') === '1');
  if (!ON) return;

  var priceFetchCache = {};   // handle -> live price (cents) from /products/<handle>.js
  var lastSignature = '';     // avoid redundant re-scans

  // -- page context: theme, template, view, sections ------------------------
  function context() {
    var t = (window.Shopify && Shopify.theme) || {};
    var tmpl = (document.body.className.match(/template--[\w-]+/g) || []).join(' ');
    var sections = [].slice.call(document.querySelectorAll('.shopify-section'))
      .map(function (s) {
        var inner = s.querySelector('[data-section-type]');
        return { id: s.id.replace('shopify-section-', ''),
                 type: inner ? inner.getAttribute('data-section-type') : '' };
      });
    return {
      theme: t.name + ' (id ' + t.id + ', ' + t.role + ')',
      template: tmpl || '(unknown)',
      view: new URLSearchParams(location.search).get('view') || '(default)',
      money_format: window.Shopify && Shopify.money_format,
      sections: sections
    };
  }

  // -- find the real card container (NOT the *__link anchor) -----------------
  function cardOf(a) {
    var card = a.closest('.product-card, .card-wrapper, .card, li.grid__item, [class*="grid__item"]');
    // if we landed on an inner "...__link" / "...__media" wrapper, climb to the real card
    while (card && /__(link|media|image|inner)\b/.test(card.className)) {
      var up = card.parentElement && card.parentElement.closest('.product-card, .card-wrapper, .card, li.grid__item');
      if (!up || up === card) break;
      card = up;
    }
    return card;
  }

  // visible price text only (drop the .visually-hidden duplicate Dawn/Forest adds)
  function shownPrice(card) {
    var block = card.querySelector('.price');
    if (!block) return '(no .price)';
    var clone = block.cloneNode(true);
    [].slice.call(clone.querySelectorAll('.visually-hidden')).forEach(function (n) { n.remove(); });
    return clone.textContent.replace(/\s+/g, ' ').trim().slice(0, 30) || '(empty)';
  }

  // -- find product cards on the page ---------------------------------------
  function cards() {
    var seen = {}, list = [];
    [].slice.call(document.querySelectorAll('a[href*="/products/"]')).forEach(function (a) {
      var handle = (a.getAttribute('href').split('?')[0].split('/products/')[1] || '').split('/')[0];
      if (!handle || seen[handle]) return;
      var card = cardOf(a);
      if (!card) return;
      seen[handle] = 1;
      var pc = card.querySelector('.price__container');
      var priceEl = card.querySelector('.product-card__price, .price');
      var inGlobo = !!card.closest('[class*="globo"],[class*="gspf"],[id*="globo"],[class*="gf-"]');
      list.push({
        handle: handle,
        shown: shownPrice(card),
        dataPrice: pc ? pc.getAttribute('data-price') : null,          // cents Liquid emitted — the smoking gun
        moneyFmt: pc ? pc.getAttribute('data-money-format') : null,
        soldOut: /sold-out/.test(card.className) ? 'yes' : '',
        renderer: inGlobo ? 'Globo-rendered' : (pc && pc.hasAttribute('data-price') ? 'theme price.liquid' : 'custom / no data-price'),
        inGlobo: inGlobo,
        cardTag: card.tagName.toLowerCase(),
        priceHTML: priceEl ? priceEl.outerHTML.replace(/\s+/g, ' ').slice(0, 500) : '(no price el)',
        section: (card.closest('.shopify-section') || {}).id || ''
      });
    });
    return list;
  }

  // -- real price from Shopify (source of truth) ----------------------------
  function livePrice(handle) {
    if (priceFetchCache[handle] !== undefined) return Promise.resolve(priceFetchCache[handle]);
    return fetch('/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { return (priceFetchCache[handle] = j ? j.price : null); })
      .catch(function () { return (priceFetchCache[handle] = null); });
  }

  function centsToStr(c) { return c == null ? '?' : '$' + (c / 100).toFixed(2); }

  // -- main scan ------------------------------------------------------------
  function scan() {
    var ctx = context();
    var list = cards();
    var sig = list.map(function (c) { return c.handle + c.shown; }).join('|');
    if (sig === lastSignature) return;            // nothing changed
    lastSignature = sig;

    Promise.all(list.map(function (c) {
      return livePrice(c.handle).then(function (live) {
        c.live = centsToStr(live);
        c.liveCents = live;
        // mismatch if the shown text doesn't contain the real amount
        c.MISMATCH = (live != null && c.shown.indexOf((live / 100).toFixed(2)) === -1) ? '❌' : '';
        return c;
      });
    })).then(function (rows) {
      var bad = rows.filter(function (r) { return r.MISMATCH; });

      console.group('%cICHS DEBUG', 'background:#1B3A5C;color:#fff;padding:2px 6px;border-radius:3px');
      console.log('Theme:    ', ctx.theme);
      console.log('Template: ', ctx.template, '  view=', ctx.view);
      console.log('Money fmt:', ctx.money_format);
      console.table(ctx.sections);
      console.table(rows.map(function (r) {
        return { handle: r.handle, shown: r.shown, real: r.live, mismatch: r.MISMATCH,
                 dataPrice_cents: r.dataPrice, soldOut: r.soldOut, moneyFmt: r.moneyFmt,
                 renderer: r.renderer, section: r.section };
      }));
      if (bad.length) console.warn('MISMATCHES (shown ≠ real):', bad);
      console.groupEnd();

      panel(ctx, rows, bad);
    });
  }

  // -- on-screen panel ------------------------------------------------------
  function panel(ctx, rows, bad) {
    var el = document.getElementById('ichs-debug-panel');
    if (!el) {
      el = document.createElement('div');
      el.id = 'ichs-debug-panel';
      el.style.cssText = 'position:fixed;bottom:10px;right:10px;z-index:99999;max-width:360px;' +
        'max-height:50vh;overflow:auto;background:#1B3A5C;color:#fff;font:11px/1.4 monospace;' +
        'padding:10px 12px;border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,.4)';
      document.body.appendChild(el);
    }
    el.innerHTML =
      '<b>ICHS DEBUG</b> &nbsp;<span style="opacity:.7;cursor:pointer" onclick="this.closest(\'#ichs-debug-panel\').remove()">✕</span><br>' +
      'Theme: ' + ctx.theme + '<br>' +
      'Template: ' + ctx.template + ' &nbsp;view=' + ctx.view + '<br>' +
      'Cards: ' + rows.length + ' &nbsp; Mismatches: <b style="color:' + (bad.length ? '#ff8a8a' : '#8affb0') + '">' + bad.length + '</b>' +
      (bad.length ? '<hr style="border:none;border-top:1px solid rgba(255,255,255,.2)">' +
        bad.slice(0, 8).map(function (b) {
          return '❌ ' + b.handle.slice(0, 30) + '<br>&nbsp;&nbsp;shown <b>' + b.shown +
                 '</b> · real <b>' + b.live + '</b><br>&nbsp;&nbsp;data-price <b>' + b.dataPrice +
                 '</b> cents · ' + b.renderer + (b.soldOut ? ' · sold-out' : '');
        }).join('<br>') : '');
  }

  // -- run now + re-run when the grid mutates (Globo swaps it in) ------------
  function boot() {
    scan();
    var grid = document.querySelector('#main-collection-product-grid, [id*="product-grid"], main') || document.body;
    new MutationObserver(function () { clearTimeout(boot._t); boot._t = setTimeout(scan, 600); })
      .observe(grid, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();